import * as STD from '@emersya/mpa-std-lib';
import CM       from '@emersya/mpa-components-manager-lib';


/**
 * A module is a piece of code adding some features to the engine.
 * Modules load order :
 * 	-Instantiate all modules.
 * 	-Retrieves all used modules:
 * 		-for each : initialize.
 * 		-for each : finalize.
 * 		-for each : run.
 **/
abstract class EngineModule<TType extends ('ui' | 'logical'), TConfiguration> {
	static all : EngineModule.TModule[] = [];
	static used : EngineModule.TModule[] = [];
	static usedWithPageOrdered : EngineModule.UIEngineModule<any>[] = [];
	static disableds : string[] = [];

	// This method SHOULD BE called in ApplicationComponent.component_initialize only!
	static async use(modulesInfos : EngineModule.IModuleInfos[])
	{
		if (EngineModule.used.length > 0) {
			throw STD.errors.user('Use method can only be called one time');
		}
		const modules = modulesInfos.
		map<[ string, unknown, EngineModule.TModule | null, EngineModule.TTranslation ]>(infos => [
			infos.id,
			infos.configuration,
			this.all.find(x => x.id === infos.id) || null,
			infos.translations
		]).
		reduce((a, [ moduleId, configuration, module, translations ]) => {
			if (!module) {
				a.notFound.push(moduleId);
			} else {
				a.found.push([ configuration, module, translations ]);
			}
			return a;
		}, { found : <[ unknown, EngineModule.TModule, EngineModule.TTranslation ][]> [], notFound : <string[]> [] });
		if (modules.notFound.length) {
			throw STD.errors.user(`Theses modules are unknown : ${ modules.notFound.join(', ') }!`);
		}
		if (new Set(modules.found).size !== modulesInfos.length) {
			const duplicates = modules.found.filter((x, xi, list) => list.lastIndexOf(x) !== xi);
			throw STD.errors.user(`Theses modules appears twice : ${ duplicates.join(', ') }!`);
		}
		for (const [ configuration, m, trs ] of modules.found) {
			if (m.allowedCustomers && !m.allowedCustomers.includes(app.clientAppParameters.app.customerCode)) {
				throw STD.errors.user(`This module is unknown : ${ m.id }!`);
			}
			await m.initialize(configuration, trs);
		}
		this.usedWithPageOrdered = (() => {
			const withPage = modules.found.filter<[ unknown, EngineModule.UIEngineModule<any>, EngineModule.TTranslation ]>(<(value : any) => value is [ unknown, EngineModule.UIEngineModule<any>, EngineModule.TTranslation ]> (x => x[ 1 ].type ===
				'ui'));
			const map = STD.object.fromKeyValueList(withPage.map(x => [
				x[ 1 ].id,
				x[ 1 ].modulesBefore
			]));
			for (const d of withPage) {
				for (const after of d[ 1 ].modulesAfter) {
					const node = map[ after ];
					node.push(d[ 1 ].id);
				}
			}
			const visited = new Set<string>;
			const stack : string[] = [];

			function dfs(node : string) {
				if (visited.has(node)) {
					return;
				}
				visited.add(node);
				const neighbours = map[ node ];
				if (!neighbours) {
					throw STD.errors.user(`Unknown module "${ node }"!`, { moduleId : node });
				}
				for (const next of neighbours) {
					dfs(next);
				}
				stack.push(node);
			}

			for (const node of Object.keys(map)) {
				dfs(node);
			}
			return stack.map(x => <EngineModule.UIEngineModule<any>> this.used.find(y => y.id === x) || (() => {
				throw new Error;
			})());
		})();
		for (const m of modules.found) {
			await m[ 1 ].finalize();
		}
		for (const m of modules.found) {
			await m[ 1 ].run();
		}
	};

	readonly type : TType;
	readonly id : string;
	readonly dependencies : string[];
	readonly configuration! : TConfiguration; // Set on initialize.
	readonly translations! : {  // Set on initialize.
		readonly '@name' : STD.translationsManager.TranslationMapping;
		readonly [ key : string ] : STD.translationsManager.TranslationMapping;
	};
	isUsed = false;
	private methods : Record<string, () => Promise<void> | void> = {};
	readonly allowedCustomers : string[] | null;
	readonly component! : CM.Component<any, any> | null;

	async initialize(configuration : TConfiguration, translations : EngineModule.TTranslation)
	{
		this.isUsed = true;
		(<any> this).configuration = configuration;
		(<any> this).translations = translations;
		EngineModule.used.push(<EngineModule.TModule> <any> this);
		if (this.methods.initialize) {
			await this.methods.initialize.call(this);
		}
	};

	async finalize() {
		for (const dependency of this.dependencies) {
			if (!EngineModule.all.find(x => x.id === dependency)) {
				throw STD.errors.user(`Missing dependency "${ dependency }" for module "${ this.id }"!`);
			}
		}
		;
		if (this.methods.finalize) {
			await this.methods.finalize.call(this);
		}
	};

	async run() {
		if (this.methods.run) {
			await this.methods.run.call(this);
		}
	};

	constructor(descriptor : EngineModule.TDescriptor) {
		this.id = descriptor.id;
		this.dependencies = descriptor.dependencies;
		this.type = <TType> descriptor.type;
		this.allowedCustomers = descriptor.allowedCustomers;
		if (EngineModule.all.find(x => x.id === this.id)) {
			throw STD.errors.user('This modules has already been instanced : ' + this.id);
		}
		if (this.type !== 'ui' && this.type !== 'logical') {
			throw STD.errors.user('');
		}
		EngineModule.all.push(<EngineModule.TModule> <any> this);
		if (descriptor.initialize) {
			this.methods.initialize = descriptor.initialize;
		}
		if (descriptor.finalize) {
			this.methods.finalize = descriptor.finalize;
		}
		if (descriptor.run) {
			this.methods.run = descriptor.run;
		}
	};
};

namespace EngineModule {

	export interface IDescriptor<TType extends ('ui' | 'logical')> {
		readonly type : TType;
		readonly id : string;
		readonly dependencies : string[];
		readonly initialize? : (this : EngineModule<any, any>) => Promise<void> | void;
		readonly finalize? : (this : EngineModule<any, any>) => Promise<void> | void;
		readonly run? : (this : EngineModule<any, any>) => Promise<void> | void;
		readonly allowedCustomers : string[] | null;
	};

	export interface IUIModuleDescriptor extends IDescriptor<'ui'> {
		readonly modulesBefore : string[];
		readonly modulesAfter : string[];

		readonly displayCondition : CM.Condition.TSerialized | null;
		readonly usableCondition : CM.Condition.TSerialized | null;

		readonly generateComponentConfiguration : (this : EngineModule.UIEngineModule<any>) => Promise<CM.DOMComponent.IConfiguration> | CM.DOMComponent.IConfiguration;
		readonly disabledByDefault? : boolean;
	};

	export interface ILogicalModuleDescriptor extends IDescriptor<'logical'> {
		readonly insertionPath : string | null; /* component path */
		readonly generateComponentConfiguration : ((this : EngineModule.UIEngineModule<any>) => Promise<CM.Component.IConfiguration> | CM.Component.IConfiguration) | null;
	};

	export type TDescriptor = IUIModuleDescriptor | ILogicalModuleDescriptor;
	export type TModule = UIEngineModule<any> | LogicalEngineModule<any>;

	export interface TTranslation {
		readonly '@name'? : STD.translationsManager.TranslationMapping;

		readonly [ key : string ] : STD.translationsManager.TranslationMapping | undefined;
	};

	export interface IModuleInfos {
		readonly  id : string;
		readonly configuration : Record<string, unknown>;
		readonly translations : TTranslation;
	};

	export class UIEngineModule<TConfiguration> extends EngineModule<'ui', TConfiguration> {
		readonly modulesBefore : string[];
		readonly modulesAfter : string[];
		readonly displayCondition : CM.Condition.TCondition | null;
		readonly usableCondition : CM.Condition.TCondition | null;
		conditionsStates : {
			isDisplayable : boolean;
			isUsable : boolean;
		};
		readonly generateComponentConfiguration : () => Promise<CM.DOMComponent.IConfiguration> | CM.DOMComponent.IConfiguration;
		declare component : CM.DOMComponent<any, any, any>; // Filled by the engineModule component.

		private evaluateCondition(condition : CM.Condition.TCondition, useDebug = false) {
			const report : any[] = [];
			const result = condition.evaluate(app, report);
			if (useDebug) {
				console.log({ condition, report, result });
			}
			return result;
		};

		async evaluateConditions(useDebug = false) {
			if (!this.displayCondition && !this.usableCondition) {
				return;
			}
			const isDisplayable = this.displayCondition ? this.evaluateCondition(this.displayCondition, useDebug) : true;
			const isUsable = this.usableCondition ? this.evaluateCondition(this.usableCondition, useDebug) : true;
			const sectionIconsItem = <CM.DOMComponent<any, any, any>> app.component_find(`@modules:sectionsIcons.${ this.id }`);
			const panelItem = <CM.DOMComponent<any, any, any>> app.component_find(`@modules:panel.${ this.id }`);
			const shouldBeDisabled = !isDisplayable || !isUsable;
			const isCurrentlyDisabled = !this.conditionsStates.isDisplayable || !this.conditionsStates.isUsable;
			if (isDisplayable !== this.conditionsStates.isDisplayable) {
				if (isDisplayable) {
					sectionIconsItem.component_DOM.classList.remove('undisplayable');
					panelItem.component_DOM.classList.remove('undisplayable');
				} else {
					sectionIconsItem.component_DOM.classList.add('undisplayable');
					panelItem.component_DOM.classList.add('undisplayable');
				}
				this.conditionsStates.isDisplayable = isDisplayable;
			}
			if (isUsable !== this.conditionsStates.isUsable) {
				if (isUsable) {
					sectionIconsItem.component_DOM.classList.remove('unusable');
					panelItem.component_DOM.classList.remove('unusable');
				} else {
					sectionIconsItem.component_DOM.classList.add('unusable');
					panelItem.component_DOM.classList.add('unusable');
				}
				this.conditionsStates.isUsable = isUsable;
			}
			if (shouldBeDisabled !== isCurrentlyDisabled) {
				if (shouldBeDisabled) {
					await sectionIconsItem.component_disable();
					await panelItem.component_disable();
				} else {
					await sectionIconsItem.component_enable();
					await panelItem.component_enable();
				}
			}
		};

		constructor(descriptor : EngineModule.IUIModuleDescriptor) {
			super(descriptor);
			this.modulesBefore = descriptor.modulesBefore;
			this.modulesAfter = descriptor.modulesAfter;
			this.generateComponentConfiguration = descriptor.generateComponentConfiguration;
			this.displayCondition = descriptor.displayCondition ? CM.Condition.deserialize(descriptor.displayCondition) : null;
			this.usableCondition = descriptor.usableCondition ? CM.Condition.deserialize(descriptor.usableCondition) : null;
			this.conditionsStates = {
				isDisplayable : true,
				isUsable :      true
			};
		};
	};

	export class LogicalEngineModule<TConfiguration> extends EngineModule<'logical', TConfiguration> {
		readonly insertionPath : string | null; /* component path */
		readonly generateComponentConfiguration : (() => Promise<CM.Component.IConfiguration> | CM.Component.IConfiguration) | null;
		override component : CM.Component<any, any> | null = null; // Filled by the engineModule component.

		constructor(descriptor : EngineModule.ILogicalModuleDescriptor) {
			super(descriptor);
			this.insertionPath = descriptor.insertionPath;
			this.generateComponentConfiguration = descriptor.generateComponentConfiguration;
		};
	};


}
;
export default EngineModule;
